import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Pipe, Inject, NgModule } from '@angular/core';
import { clamp } from '@coyoapp/ui/cdk';

const CUI_FILE_ICON_DEFAULT_CONFIG = {
    default: {
        'default': 'generic-file',
        '^application\\/pdf$': 'pdf',
        '^application\\/zip$': 'mc-zip-archive',
        '^text\\/plain$': 'text',
        '^image\\/': 'mc-image',
        '^video\\/': 'video'
    }
};
const CUI_FILE_ICON_CONFIG = new InjectionToken('CUI_FILE_ICON_CONFIG', {
    factory: () => CUI_FILE_ICON_DEFAULT_CONFIG
});

/**
 * Pipe that transforms a MIME type into a corresponding icon name.
 */
class FileIconPipe {
    constructor(config) {
        this.config = config;
    }
    transform(mimeType, setName = 'default') {
        return this.getIconFromSet(setName, mimeType)
            || this.getIconFromSet(FileIconPipe.DEFAULT_ICON_SET_KEY, mimeType)
            || this.config[FileIconPipe.DEFAULT_ICON_SET_KEY]?.[FileIconPipe.DEFAULT_ICON_KEY]
            || FileIconPipe.DEFAULT_ICON;
    }
    getIconFromSet(setName, mimeType) {
        const set = this.config[setName] || {};
        return Object.entries(set).find(([regex]) => new RegExp(regex).test(mimeType))?.[1];
    }
}
FileIconPipe.DEFAULT_ICON_SET_KEY = 'default';
FileIconPipe.DEFAULT_ICON_KEY = 'default';
FileIconPipe.DEFAULT_ICON = 'generic-file';
FileIconPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileIconPipe, deps: [{ token: CUI_FILE_ICON_CONFIG }], target: i0.ɵɵFactoryTarget.Pipe });
FileIconPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileIconPipe, name: "fileIcon" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileIcon'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CUI_FILE_ICON_CONFIG]
                }] }]; } });

/**
 * Pipe that returns file name without extension or path from a full path
 */
class FileNamePipe {
    transform(file, options) {
        const opts = { ...FileNamePipe.DEFAULTS, ...options };
        const fullPathParts = file.split('/');
        const fileNameParts = fullPathParts[fullPathParts.length - 1].split('.');
        const returnedFileName = [];
        returnedFileName[0] = opts.stripPath ? '' : fullPathParts.slice(0, -1).join('/');
        if (returnedFileName[0]) {
            returnedFileName[0] += '/';
        }
        returnedFileName[1] = opts.stripExt ? fileNameParts[0] : fileNameParts.join('.');
        return `${returnedFileName[0]}${returnedFileName[1]}`;
    }
}
FileNamePipe.DEFAULTS = {
    stripPath: false,
    stripExt: true
};
FileNamePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileNamePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FileNamePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileNamePipe, name: "fileName" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileNamePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileName'
                }]
        }] });

/**
 * Pipe that displays sizes in bytes in human readable format
 */
class FileSizePipe {
    transform(size, separator = '') {
        const result = [];
        const num = Math.abs(+size);
        const e = clamp(Math.floor(Math.log(num) / Math.log(1000)), 0, 8);
        if (num === 0) {
            result[0] = 0;
            result[1] = FileSizePipe.SYMBOLS[e];
        }
        else {
            const val = num / Math.pow(1000, e);
            const p = Math.pow(10, e > 0 ? 1 : 0);
            result[0] = Math.round(val * p) / p;
            result[1] = FileSizePipe.SYMBOLS[e];
        }
        return `${Math.abs(result[0])}${separator}${result[1]}`;
    }
}
FileSizePipe.SYMBOLS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
FileSizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FileSizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileSizePipe, name: "fileSize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileSizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileSize'
                }]
        }] });

class FileModule {
}
FileModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FileModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileModule, declarations: [FileIconPipe, FileNamePipe, FileSizePipe], imports: [CommonModule], exports: [FileIconPipe, FileNamePipe, FileSizePipe] });
FileModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FileModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FileIconPipe, FileNamePipe, FileSizePipe],
                    exports: [FileIconPipe, FileNamePipe, FileSizePipe]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CUI_FILE_ICON_CONFIG, CUI_FILE_ICON_DEFAULT_CONFIG, FileIconPipe, FileModule, FileNamePipe, FileSizePipe };
